import { useState } from "react";
import { AxiosResponse } from "axios";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import {
  downloadBlob,
  getFilenameFromContentDisposition,
} from "../SalesReportExport/utils";

interface ExportButtonProps {
  /** Fires the export request; must be made with `responseType: "blob"`. */
  request: () => Promise<AxiosResponse<Blob>>;
  /** Used only when the server's `Content-Disposition` is missing or unparseable. */
  fallbackFilename: string;
  label?: string;
  disabled?: boolean;
  /**
   * Render as a bare icon instead of a labelled button — for a table's action
   * column, where a spelled-out label costs the row width the data needs. The
   * label then rides in the tooltip, so the action still says what it is.
   */
  iconOnly?: boolean;
}

/**
 * Downloads a server-built file for a list page — every row the current filters match,
 * not just the page on screen.
 *
 * Errors are left to the axios interceptor: it rehydrates a blob error body back into
 * JSON, so the backend's own message (e.g. "too many rows, narrow the filter") reaches
 * the global toast without this component parsing it a second time.
 */
export default function ExportButton({
  request,
  fallbackFilename,
  label = "Export Excel",
  disabled = false,
  iconOnly = false,
}: Readonly<ExportButtonProps>) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await request();
      const filename =
        getFilenameFromContentDisposition(
          response.headers["content-disposition"],
        ) || fallbackFilename;
      downloadBlob(filename, response.data);
    } catch {
      // Already surfaced by the axios interceptor.
    } finally {
      setExporting(false);
    }
  };

  if (iconOnly) {
    return (
      // Tooltip cannot hang off a disabled button, hence the span.
      <Tooltip title={label}>
        <span>
          <IconButton
            size="small"
            color="success"
            aria-label={label}
            onClick={handleExport}
            disabled={disabled || exporting}
          >
            {exporting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <DownloadIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Button
      variant="outlined"
      startIcon={
        exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
      }
      onClick={handleExport}
      disabled={disabled || exporting}
      sx={{ whiteSpace: "nowrap" }}
    >
      {label}
    </Button>
  );
}
